import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Image, Row, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorToast, SuccessToast, WarningToast } from '../../../utils/toasters';
import { IPackages } from '../../../model/package/IPackage';
import { multipartFormData } from '../../../api/api.service';
import { PackagesService } from '../PackagesService';
import { PACKAGES_URL } from '../../../utils/Endpoints';
import { useSelector } from 'react-redux';
import { getCorrectEnums } from '../../../utils/Functions';
import { calculateFee } from '../../../utils/helpers/FeeCaluculation';
import { calculateProfilePrice, hasLeftover } from '../../../utils/helpers/DiscoveryProfilePriceCalculation';
import CardLayout from '../../../shared/components/CardLayout';
import ReactQuill from 'react-quill';

type HighlightCardProps = {
    children: React.ReactNode;
    className?: string;
};

// ─── Reusable section header ───────────────────────────────────────────────
function SectionTitle({ title, badge }: { title: string; badge?: string }) {
    return (
        <div className="section-title d-flex align-items-center gap-2 mb-4 mt-2">
            <h5 className="section-title-text text-uppercase fw-bold mb-0 text-muted">{title}</h5>
            {badge && (
                <Badge bg="warning" text="dark" className="section-title-badge">
                    {badge}
                </Badge>
            )}
            <div className="flex-grow-1 border-bottom ms-2" />
        </div>
    );
}

// ─── Highlighted card wrapper ──────────────────────────────────────────────

function HighlightCard({ children, className = '' }: HighlightCardProps) {
    return <div className={`highlight-card rounded p-4 mb-4 ${className}`}>{children}</div>;
}
export default function PackagesForm() {
    const { packageId } = useParams();
    const navigate = useNavigate();

    const {
        PackagesEnum,
        SocialPackageEnum,
        SearchPackageEnum,
        BriefingPackageEnum,
        BookPackageEnum,
        ManagePackageEnum,
        MeasurePackageEnum,
        MessagePackageEnum,
        SuccessPackageEnum,
        CustomerPackageEnum,
        TechSupportPackageEnum,
        CreatorSourcingPackageEnum,
        PaymentTermsEnum,
    } = useSelector((state: any) => state.enums);

    const [packageData, setPackageData] = useState<Partial<IPackages>>({
        isActive: false,
        name: 'small',
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePath, setImage] = useState('');

    useEffect(() => {
        if (packageId) {
            PackagesService.getPackage(+packageId)
                .then(response => {
                    setPackageData(response);
                    if (response?.image) setImage(response?.image?.path);
                })
                .catch(err => ErrorToast(err));
        }
    }, []);

    const createPackage = () => {
        if (!!packageData.name) {
            multipartFormData(selectedFile, PACKAGES_URL, JSON.stringify(packageData), 'post')
                .then(() => {
                    SuccessToast('Successfully created package');
                    navigate('/packages/packages');
                })
                .catch(error => ErrorToast(error));
        } else {
            WarningToast('Please fill out all mandatory fields');
        }
    };

    const updatePackage = () => {
        multipartFormData(selectedFile, `${PACKAGES_URL}/${packageId}`, JSON.stringify(packageData))
            .then(() => {
                SuccessToast('Successfully updated package');
                navigate('/packages/packages');
            })
            .catch(error => ErrorToast(error));
    };
    function handleChange(event: any) {
        const { value, name, type } = event.target;
        let parsedValue: any = value;

        if (name === 'fee') {
            parsedValue = +value > 1 || +value < 0 ? 1 : +value;
        }

        if (name === 'savingPercentage') {
            parsedValue = value.replace(/^0+/, '') || '0';
            let num = parseInt(parsedValue, 10);
            if (isNaN(num) || num < 0) num = 0;
            if (num > 100) num = 100;
            parsedValue = num.toString();
        }

        setPackageData(prev => ({ ...prev, [name]: parsedValue }));
    }

    function handleFileInput(evt: any) {
        if (evt.target.files.length > 0) {
            setSelectedFile(evt.target.files[0]);
            setImage(URL.createObjectURL(evt.target.files[0]));
        }
    }

    const isDiscoveryInvalid = () => {
        if (!packageData.canDoDiscovery) return false;
        return (
            hasLeftover(packageData.discoveryTokens, packageData.discoveryProfilePrice) ||
            hasLeftover(packageData.discoverySearchTokens, packageData.discoverySearchPrice)
        );
    };

    return (
        <CardLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="fw-bold mb-0">{packageId ? 'Update Package' : 'Create Package'}</h4>
                <Button
                    disabled={isDiscoveryInvalid()}
                    onClick={packageId ? updatePackage : createPackage}
                    variant="primary"
                    size="sm"
                    className="px-4"
                >
                    Save changes
                </Button>
            </div>
            <hr className="mb-4" />

            <Form>
                <Row>
                    {/* ── LEFT COLUMN ────────────────────────────────── */}
                    <Col md={8} className="pr-8">
                        {/* GENERAL */}
                        <SectionTitle title="General" />
                        <Row className="mb-4">
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Key</Form.Label>
                                <Form.Select value={packageData?.name} name="name" onChange={handleChange}>
                                    <option />
                                    {PackagesEnum?.map((item: string) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Display name</Form.Label>
                                <Form.Control
                                    name="displayName"
                                    onChange={handleChange}
                                    value={packageData?.displayName}
                                />
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Annual Price (€)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="annualPrice"
                                    onChange={handleChange}
                                    value={packageData.annualPrice}
                                />
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Monthly Price (€)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="monthlyPrice"
                                    onChange={handleChange}
                                    value={packageData.monthlyPrice}
                                />
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Admin accounts</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="nbMasters"
                                    onChange={handleChange}
                                    value={packageData.nbMasters}
                                />
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Manager accounts</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="nbEmployees"
                                    onChange={handleChange}
                                    value={packageData.nbEmployees}
                                />
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Position</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="position"
                                    onChange={handleChange}
                                    value={packageData.position}
                                />
                            </Form.Group>

                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">
                                    Fee{' '}
                                    <span className="text-muted fw-normal">
                                        (currently: {calculateFee(packageData.fee)})
                                    </span>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    max={1}
                                    name="fee"
                                    onChange={handleChange}
                                    value={packageData.fee ?? 0.0}
                                />
                            </Form.Group>

                            <Col xs={12} className="mt-2">
                                <div className="d-flex flex-wrap gap-4">
                                    {[
                                        { label: 'Active', key: 'isActive' },
                                        { label: 'Shown to user', key: 'isShownToUser' },
                                        { label: 'Transparent pricing', key: 'isTransparentPricing' },
                                        { label: 'Can do long-term', key: 'canDoLongTerm' },
                                    ].map(({ label, key }) => (
                                        <Form.Check
                                            key={key}
                                            label={label}
                                            checked={!!(packageData as any)[key]}
                                            onChange={(e: any) =>
                                                setPackageData({ ...packageData, [key]: e.target.checked })
                                            }
                                        />
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        {/* DISCOVERY */}
                        <SectionTitle title="Discovery" />
                        <Row className="mb-2">
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">
                                    Discovery tokens{' '}
                                    {(packageData.canDoDiscovery || packageData.canViewDiscoveryBundle) && (
                                        <span className="text-muted fw-normal">
                                            (
                                            {calculateProfilePrice(
                                                packageData.discoveryTokens,
                                                packageData.discoveryProfilePrice
                                            )}{' '}
                                            views)
                                        </span>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="discoveryTokens"
                                    step="1"
                                    min="0"
                                    onChange={handleChange}
                                    disabled={!packageData.canDoDiscovery && !packageData.canViewDiscoveryBundle}
                                    isInvalid={
                                        (packageData.canDoDiscovery || packageData.canViewDiscoveryBundle) &&
                                        hasLeftover(packageData.discoveryTokens, packageData.discoveryProfilePrice)
                                    }
                                    onKeyDown={(e: any) => {
                                        if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                    value={packageData.discoveryTokens ?? 0}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Must divide evenly by profile view price.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Profile view price</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="discoveryProfilePrice"
                                    step="1"
                                    min="0"
                                    onChange={handleChange}
                                    disabled={!packageData.canDoDiscovery && !packageData.canViewDiscoveryBundle}
                                    isInvalid={
                                        (packageData.canDoDiscovery || packageData.canViewDiscoveryBundle) &&
                                        hasLeftover(packageData.discoveryTokens, packageData.discoveryProfilePrice)
                                    }
                                    onKeyDown={(e: any) => {
                                        if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                    value={packageData.discoveryProfilePrice ?? 0}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Must divide evenly by profile view price.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">
                                    Search tokens{' '}
                                    {packageData.canDoDiscovery && (
                                        <span className="text-muted fw-normal">
                                            (
                                            {calculateProfilePrice(
                                                packageData.discoverySearchTokens,
                                                packageData.discoverySearchPrice
                                            )}{' '}
                                            searches)
                                        </span>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="discoverySearchTokens"
                                    step="1"
                                    min="0"
                                    onChange={handleChange}
                                    disabled={!packageData.canDoDiscovery}
                                    isInvalid={
                                        packageData.canDoDiscovery &&
                                        hasLeftover(packageData.discoverySearchTokens, packageData.discoverySearchPrice)
                                    }
                                    onKeyDown={(e: any) => {
                                        if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                    value={packageData.discoverySearchTokens ?? 0}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Must divide evenly by search price.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Search price</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="discoverySearchPrice"
                                    step="1"
                                    min="0"
                                    onChange={handleChange}
                                    disabled={!packageData.canDoDiscovery}
                                    isInvalid={
                                        packageData.canDoDiscovery &&
                                        hasLeftover(packageData.discoverySearchTokens, packageData.discoverySearchPrice)
                                    }
                                    onKeyDown={(e: any) => {
                                        if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                    value={packageData.discoverySearchPrice ?? 0}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Must divide evenly by search price.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Col xs={12}>
                                <div className="d-flex flex-wrap gap-4">
                                    <Form.Check
                                        label="Discovery search"
                                        checked={packageData.canDoDiscovery}
                                        onChange={(e: any) =>
                                            setPackageData({ ...packageData, canDoDiscovery: e.target.checked })
                                        }
                                    />
                                    <Form.Check
                                        label="Discovery bundle"
                                        checked={packageData.canViewDiscoveryBundle}
                                        onChange={(e: any) =>
                                            setPackageData({ ...packageData, canViewDiscoveryBundle: e.target.checked })
                                        }
                                    />
                                    <Form.Check
                                        label="Global discovery"
                                        checked={packageData.globalDiscovery}
                                        onChange={(e: any) =>
                                            setPackageData({ ...packageData, globalDiscovery: e.target.checked })
                                        }
                                    />
                                    {packageData.globalDiscovery && (
                                        <Form.Check
                                            label="Limit discovery"
                                            checked={packageData.limitDiscovery}
                                            onChange={(e: any) =>
                                                setPackageData({ ...packageData, limitDiscovery: e.target.checked })
                                            }
                                        />
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {packageData.globalDiscovery && !packageData.limitDiscovery && (
                            <>
                                <SectionTitle title="Trial Tokens" badge="Stripe" />
                                <HighlightCard className="highlight-card-warning">
                                    <p className="text-muted mb-3 small-text">
                                        Tokens assigned when a company starts a trial. Full tokens are granted after
                                        trial converts to paid.
                                    </p>
                                    <Row>
                                        {' '}
                                        <Form.Group className="col-6 mb-3">
                                            <Form.Label className="fw-semibold">
                                                Trial discovery tokens{' '}
                                                {(packageData.canDoDiscovery || packageData.canViewDiscoveryBundle) && (
                                                    <span className="text-muted fw-normal">
                                                        (
                                                        {calculateProfilePrice(
                                                            packageData.trialDiscoveryTokens,
                                                            packageData.discoveryProfilePrice
                                                        )}{' '}
                                                        views)
                                                    </span>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="trialDiscoveryTokens"
                                                step="1"
                                                min="0"
                                                onChange={handleChange}
                                                disabled={
                                                    !packageData.canDoDiscovery && !packageData.canViewDiscoveryBundle
                                                }
                                                isInvalid={
                                                    (packageData.canDoDiscovery ||
                                                        packageData.canViewDiscoveryBundle) &&
                                                    hasLeftover(
                                                        packageData.trialDiscoveryTokens,
                                                        packageData.discoveryProfilePrice
                                                    )
                                                }
                                                onKeyDown={(e: any) => {
                                                    if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                                }}
                                                value={packageData.trialDiscoveryTokens ?? 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Must divide evenly by profile view price.
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="col-6 mb-3">
                                            <Form.Label className="fw-semibold">
                                                Trial search tokens{' '}
                                                {packageData.canDoDiscovery && (
                                                    <span className="text-muted fw-normal">
                                                        (
                                                        {calculateProfilePrice(
                                                            packageData.trialDiscoverySearchTokens,
                                                            packageData.discoverySearchPrice
                                                        )}{' '}
                                                        searches)
                                                    </span>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="trialDiscoverySearchTokens"
                                                step="1"
                                                min="0"
                                                onChange={handleChange}
                                                disabled={!packageData.canDoDiscovery}
                                                isInvalid={
                                                    packageData.canDoDiscovery &&
                                                    hasLeftover(
                                                        packageData.trialDiscoverySearchTokens,
                                                        packageData.discoverySearchPrice
                                                    )
                                                }
                                                onKeyDown={(e: any) => {
                                                    if (['.', ',', 'e'].includes(e.key)) e.preventDefault();
                                                }}
                                                value={packageData.trialDiscoverySearchTokens ?? 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Must divide evenly by search price.
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Row>
                                </HighlightCard>

                                <SectionTitle title="Stripe Price IDs" badge="Stripe" />
                                <HighlightCard className="highlight-card-success">
                                    <p className="text-muted mb-3 small-text">
                                        Link this package to Stripe prices. Find price IDs in{' '}
                                        <strong>Stripe Dashboard → Products → Pricing</strong>.
                                    </p>
                                    <Row>
                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Annual Price ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="stripeAnnualPriceId"
                                                onChange={handleChange}
                                                value={packageData.stripeAnnualPriceId ?? ''}
                                                className="monospace-input"
                                            />
                                        </Form.Group>

                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Monthly Price ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="stripeMonthlyPriceId"
                                                onChange={handleChange}
                                                value={packageData.stripeMonthlyPriceId ?? ''}
                                                className="monospace-input"
                                            />
                                        </Form.Group>

                                        {/* ANNUAL EN */}
                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Annual Description (EN)</Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={packageData?.annualDescEn || ''}
                                                onChange={value =>
                                                    handleChange({
                                                        target: { name: 'annualDescEn', value },
                                                    })
                                                }
                                                modules={{
                                                    toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }]],
                                                }}
                                            />
                                        </Form.Group>

                                        {/* MONTHLY EN */}
                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Monthly Description (EN)</Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={packageData?.monthlyDescEn || ''}
                                                onChange={value =>
                                                    handleChange({
                                                        target: { name: 'monthlyDescEn', value },
                                                    })
                                                }
                                                modules={{
                                                    toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }]],
                                                }}
                                            />
                                        </Form.Group>

                                        {/* ANNUAL DE */}
                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Annual Description (DE)</Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={packageData?.annualDescDe || ''}
                                                onChange={value =>
                                                    handleChange({
                                                        target: { name: 'annualDescDe', value },
                                                    })
                                                }
                                                modules={{
                                                    toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }]],
                                                }}
                                            />
                                        </Form.Group>

                                        {/* MONTHLY DE */}
                                        <Form.Group className="col-6 mb-0">
                                            <Form.Label className="fw-semibold">Monthly Description (DE)</Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={packageData?.monthlyDescDe || ''}
                                                onChange={value =>
                                                    handleChange({
                                                        target: { name: 'monthlyDescDe', value },
                                                    })
                                                }
                                                modules={{
                                                    toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }]],
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="col-6">
                                            <Form.Label className="fw-semibold">Annual saving percentage</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={0}
                                                step={1}
                                                max={100}
                                                name="savingPercentage"
                                                onChange={handleChange}
                                                value={packageData.savingPercentage ?? 0}
                                            />
                                        </Form.Group>
                                    </Row>
                                </HighlightCard>
                                <Row></Row>
                            </>
                        )}

                        {/* FUNCTIONAL */}
                        <SectionTitle title="Functional" />
                        <Row className="mb-4">
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Creator Sourcing</Form.Label>
                                <Form.Select
                                    value={packageData?.creatorSourcing}
                                    name="creatorSourcing"
                                    onChange={handleChange}
                                >
                                    <option />
                                    {CreatorSourcingPackageEnum?.map((item: string) => (
                                        <option key={item} value={item}>
                                            {getCorrectEnums(item)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Publication period length (weeks)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="publicationPeriodLength"
                                    onChange={handleChange}
                                    value={packageData.publicationPeriodLength ?? 0}
                                />
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Payment terms duration</Form.Label>
                                <Form.Select
                                    value={packageData?.paymentTerms}
                                    name="paymentTerms"
                                    onChange={handleChange}
                                >
                                    <option />
                                    {PaymentTermsEnum?.map((item: string) => (
                                        <option key={item} value={item}>
                                            {getCorrectEnums(item)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {packageData?.paymentTerms === 'other' && (
                                <Form.Group className="col-6 mb-3">
                                    <Form.Label className="fw-semibold">Payment terms description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="paymentTermsCustom"
                                        value={packageData?.paymentTermsCustom}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            )}
                            <Col xs={12} className="mt-2">
                                <div className="d-flex flex-wrap gap-4">
                                    {[
                                        { label: 'Long-term collaborations', key: 'longTermCollaborations' },
                                        { label: 'Barter deals', key: 'allowBarterDeals' },
                                        { label: 'Private campaigns', key: 'allowPrivateCampaigns' },
                                        { label: 'AI briefing', key: 'allowBriefingAI' },
                                        { label: 'Can edit payment terms', key: 'canEditPaymentTerms' },
                                    ].map(({ label, key }) => (
                                        <Form.Check
                                            key={key}
                                            label={label}
                                            checked={!!(packageData as any)[key]}
                                            onChange={(e: any) =>
                                                setPackageData({ ...packageData, [key]: e.target.checked })
                                            }
                                        />
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        {/* MODULES */}
                        <SectionTitle title="Modules" />
                        <Row className="mb-4">
                            {[
                                {
                                    label: 'Social media platforms',
                                    name: 'socialMediaPlatforms',
                                    enums: SocialPackageEnum,
                                },
                                { label: 'Search & invite', name: 'searchAndInvite', enums: SearchPackageEnum },
                                { label: 'Briefing module', name: 'briefingModule', enums: BriefingPackageEnum },
                                { label: 'Booking module', name: 'bookingModule', enums: BookPackageEnum },
                                { label: 'Manage module', name: 'manageModule', enums: ManagePackageEnum },
                                { label: 'Measure module', name: 'measureModule', enums: MeasurePackageEnum },
                                { label: 'Message module', name: 'messageModule', enums: MessagePackageEnum },
                                { label: 'Briefing feedback', name: 'briefingFeedback', enums: CustomerPackageEnum },
                                {
                                    label: 'Influencer recommendation',
                                    name: 'influencerRecommendation',
                                    enums: CustomerPackageEnum,
                                },
                                { label: 'Platform training', name: 'platformTraining', enums: TechSupportPackageEnum },
                                { label: 'Campaign feedback', name: 'campaignFeedback', enums: TechSupportPackageEnum },
                                { label: 'Success manager', name: 'successManager', enums: SuccessPackageEnum },
                            ].map(({ label, name, enums }) => (
                                <Form.Group key={name} className="col-6 mb-3">
                                    <Form.Label className="fw-semibold">{label}</Form.Label>
                                    <Form.Select value={(packageData as any)[name]} name={name} onChange={handleChange}>
                                        <option />
                                        {enums?.map((item: string) => (
                                            <option key={item} value={item}>
                                                {getCorrectEnums(item)}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            ))}
                        </Row>

                        {/* FEATURE FLAGS */}
                        <SectionTitle title="Feature Flags" />
                        <div className="d-flex flex-wrap gap-4 mb-4">
                            {[
                                { label: 'Influencer contracts', key: 'influencerContracts' },
                                { label: 'Influencer payouts', key: 'influencerPayouts' },
                                { label: 'External collaboration', key: 'externalCollaboration' },
                                { label: 'Tech support', key: 'techSupport' },
                                { label: 'Profile analytics', key: 'profileAnalytics' },
                                { label: 'Influencer reviews', key: 'reviews' },
                                { label: 'Team collaboration', key: 'teamCollaboration' },
                                { label: 'Allow campaign transfers', key: 'allowCampaignTransfers' },
                            ].map(({ label, key }) => (
                                <Form.Check
                                    key={key}
                                    label={label}
                                    checked={!!(packageData as any)[key]}
                                    onChange={(e: any) => setPackageData({ ...packageData, [key]: e.target.checked })}
                                />
                            ))}
                        </div>
                    </Col>

                    {/* RIGHT */}
                    <Col md={4}>
                        <div className="sticky-top sticky-offset">
                            <SectionTitle title="Package Image" />
                            <Form.Group className="mb-4">
                                <Form.Control accept=".jpg, .jpeg, .png" type="file" onChange={handleFileInput} />
                                {imagePath && (
                                    <Image className="mt-3 rounded preview-image" alt={imagePath} src={imagePath} />
                                )}
                            </Form.Group>

                            <SectionTitle title="Package Color" />
                            <Form.Group className="mb-4">
                                <div className="d-flex align-items-center gap-3 mb-2">
                                    <div
                                        className="color-preview"
                                        style={
                                            {
                                                '--package-color': packageData?.color || '#952cb7',
                                            } as React.CSSProperties
                                        }
                                    />
                                    <span className="text-muted monospace-text">{packageData?.color || '#952cb7'}</span>
                                </div>

                                <Form.Control
                                    type="color"
                                    name="color"
                                    value={packageData?.color || '#952cb7'}
                                    onChange={handleChange}
                                    className="color-input"
                                />
                            </Form.Group>

                            <Button
                                disabled={isDiscoveryInvalid()}
                                onClick={packageId ? updatePackage : createPackage}
                                variant="primary"
                                className="w-100 mt-2"
                            >
                                {packageId ? 'Update Package' : 'Create Package'}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </CardLayout>
    );
}
